import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Coins, CheckCircle2, Loader2, ShieldCheck } from "lucide-react";

export default function Admin() {
  const [intents, setIntents] = useState([]);
  const [users, setUsers] = useState([]);
  const [approving, setApproving] = useState(null);
  const [creditForm, setCreditForm] = useState({ email: "", credits: 100 });
  const [crediting, setCrediting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([api.get("/admin/paypal-intents"), api.get("/admin/users")]);
      setIntents(a.data);
      setUsers(b.data);
    } catch (e) {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    setApproving(id);
    try {
      await api.post(`/admin/paypal-intents/${id}/approve`);
      toast.success("Crédits accordés !");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setApproving(null);
    }
  };

  const grant = async (e) => {
    e.preventDefault();
    setCrediting(true);
    try {
      const { data } = await api.post("/admin/credit", {
        email: creditForm.email, credits: Number(creditForm.credits),
      });
      toast.success(`Crédité ! Nouveau solde : ${data.credits}`);
      setCreditForm({ email: "", credits: 100 });
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setCrediting(false);
    }
  };

  const pending = intents.filter((i) => i.status === "pending");

  return (
    <div className="studio-bg grain min-h-screen relative">
      <Navbar />
      <main className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex items-center gap-2 mb-8 fade-up">
          <ShieldCheck className="w-6 h-6 text-[#3ECF8E]" />
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Administration</h1>
        </div>

        <Tabs defaultValue="paypal">
          <TabsList className="bg-white/5 rounded-full p-1 mb-6">
            <TabsTrigger value="paypal" className="rounded-full data-[state=active]:bg-green-600 data-[state=active]:text-white" data-testid="tab-paypal-requests">
              Demandes PayPal {pending.length > 0 && <span className="ml-2 text-xs bg-lime-400 text-black rounded-full px-2">{pending.length}</span>}
            </TabsTrigger>
            <TabsTrigger value="users" className="rounded-full data-[state=active]:bg-green-600 data-[state=active]:text-white" data-testid="tab-users">
              Utilisateurs & crédits
            </TabsTrigger>
          </TabsList>

          {/* PayPal requests */}
          <TabsContent value="paypal">
            <div className="glass rounded-2xl overflow-hidden">
              {intents.length === 0 && (
                <div className="p-12 text-center text-slate-400" data-testid="no-paypal-requests">Aucune demande d'abonnement PayPal pour l'instant.</div>
              )}
              {intents.map((i) => (
                <div key={i.id} className="flex flex-wrap items-center justify-between gap-4 px-5 py-4 border-b border-white/5" data-testid={`intent-${i.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium truncate">{i.user_name || i.email}</div>
                    <div className="text-xs text-slate-500 font-mono truncate">{i.email}</div>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-slate-300">{i.plan_name}</span>
                    <span className="text-slate-500">·</span>
                    <span className="font-mono">${i.amount}</span>
                    <span className="flex items-center gap-1 text-lime-300 ml-2"><Coins className="w-4 h-4" />{i.credits}</span>
                  </div>
                  <div>
                    {i.status === "approved" ? (
                      <span className="flex items-center gap-1.5 text-emerald-400 text-sm" data-testid={`approved-${i.id}`}>
                        <CheckCircle2 className="w-4 h-4" /> Approuvé
                      </span>
                    ) : (
                      <Button onClick={() => approve(i.id)} disabled={approving === i.id}
                        className="h-9 bg-[#3ECF8E] hover:bg-[#2fb879] text-black glow-btn rounded-full text-sm px-4" data-testid={`approve-${i.id}`}>
                        {approving === i.id ? <Loader2 className="w-4 h-4 animate-spin" /> : "Approuver & créditer"}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Users & manual credit */}
          <TabsContent value="users">
            <div className="grid lg:grid-cols-[340px_1fr] gap-6">
              <form onSubmit={grant} className="glass rounded-2xl p-6 h-fit">
                <h3 className="font-display text-lg font-bold mb-4">Créditer manuellement</h3>
                <Label className="text-slate-300">Email de l'utilisateur</Label>
                <Input type="email" required value={creditForm.email} onChange={(e) => setCreditForm({ ...creditForm, email: e.target.value })}
                  className="mt-1.5 bg-white/5 border-white/10 h-11" placeholder="client@exemple.com" data-testid="credit-email-input" />
                <Label className="text-slate-300 mt-4 block">Crédits à ajouter</Label>
                <Input type="number" required value={creditForm.credits} onChange={(e) => setCreditForm({ ...creditForm, credits: e.target.value })}
                  className="mt-1.5 bg-white/5 border-white/10 h-11" data-testid="credit-amount-input" />
                <Button type="submit" disabled={crediting} className="w-full mt-5 h-11 bg-[#3ECF8E] hover:bg-[#2fb879] text-black glow-btn rounded-full font-semibold" data-testid="credit-submit-button">
                  {crediting ? <Loader2 className="w-5 h-5 animate-spin" /> : "Ajouter les crédits"}
                </Button>
              </form>

              <div className="glass rounded-2xl overflow-hidden" data-testid="users-table">
                {users.map((u) => (
                  <div key={u.id} className="flex items-center justify-between gap-4 px-5 py-3.5 border-b border-white/5">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{u.name || u.email} {u.role === "admin" && <span className="text-[10px] bg-green-600 rounded px-1.5 py-0.5 ml-1">ADMIN</span>}</div>
                      <div className="text-xs text-slate-500 font-mono truncate">{u.email}</div>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      {u.subscription_plan && <span className="text-slate-400 hidden sm:inline">{u.subscription_plan}</span>}
                      <span className="flex items-center gap-1 text-lime-300"><Coins className="w-4 h-4" />{u.credits}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
